import React from "react";
import { DataGrid as MuiDataGrid } from "@mui/x-data-grid";
import PropTypes from "prop-types";

/**
 * DataGrid component wrapper for data tables.
 * Wraps Material-UI DataGrid for consistent imports.
 * 
 * @component
 * @example
 * <DataGrid
 *   rows={rows}
 *   columns={columns}
 *   pageSize={10}
 * />
 */
const DataGrid = ({ ...props }) => {
  return <MuiDataGrid {...props} />;
};

DataGrid.propTypes = {
  /**
   * Set of rows of type GridRowsProp
   */
  rows: PropTypes.array.isRequired,
  /**
   * Set of columns of type GridColDef[]
   */
  columns: PropTypes.array.isRequired,
  /**
   * Callback fired when a cell is clicked
   */
  onCellClick: PropTypes.func,
  /**
   * If true, the grid will display checkboxes for row selection
   */
  checkboxSelection: PropTypes.bool,
  /**
   * Set the pageSize
   */
  pageSize: PropTypes.number,
  /**
   * Set the number of rows in one page
   */
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  /**
   * Callback fired when the page is changed
   */
  onPageChange: PropTypes.func,
  /**
   * Callback fired when the page size is changed
   */
  onPageSizeChange: PropTypes.func,
  /**
   * Loading state
   */
  loading: PropTypes.bool,
  /**
   * If true, the grid will display a loading overlay
   */
  autoHeight: PropTypes.bool,
  /**
   * If true, the grid will hide the footer pagination
   */
  hideFooterPagination: PropTypes.bool,
  /**
   * If true, the grid will hide the footer
   */
  hideFooter: PropTypes.bool,
  /**
   * Callback fired when the selection is changed
   */
  onSelectionModelChange: PropTypes.func,
  /**
   * The selected rows
   */
  selectionModel: PropTypes.array,
  /**
   * Callback fired when a cell is edited
   */
  onCellEditCommit: PropTypes.func,
  /**
   * The height of the rows
   */
  rowHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.func]),
  /**
   * The density of the grid
   */
  density: PropTypes.oneOf(["compact", "standard", "comfortable"]),
  /**
   * If true, the grid will disable the column menu
   */
  disableColumnMenu: PropTypes.bool,
  /**
   * If true, the grid will disable selection on click
   */
  disableSelectionOnClick: PropTypes.bool,
  /**
   * The initial state of the grid
   */
  initialState: PropTypes.object,
  /**
   * The aria-label for the grid
   */
  "aria-label": PropTypes.string,
  /**
   * Custom components
   */
  components: PropTypes.object,
  /**
   * Custom component props
   */
  componentsProps: PropTypes.object,
  /**
   * Custom slot props (MUI v6+)
   */
  slotProps: PropTypes.object,
  /**
   * Custom slots (MUI v6+)
   */
  slots: PropTypes.object,
};

export default DataGrid;
